import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useSelector } from "react-redux";

import { Info } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  Link,
  PromoBannerV2,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import macrosImage from "assets/img/macros-promo/banner@2x.png";
import workflowsImage from "assets/img/workflows-banner@2x.png";
import { newFeaturesCampaigns } from "constants/campaigns";
import { HelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { selectLoggedInAgentPermission } from "store/entities/agents";
import { withVar } from "themes/helpers";

import Flex from "../layout/Flex";

type ISection = "workflows" | "macros" | "contact forms";

interface IBannerContent {
  bannerText: { header: string; content: string };
  headerText: string;
  helpCenterLink: string;
  sectionName: string;
  eventPlace: EventPlace;
  featureFlagName: string;
  campaignStartDate: string;
  image?: string;
}

const placementProperties: Record<ISection, IBannerContent> = {
  macros: {
    bannerText: {
      header: "What are macros?",
      content:
        "Macros are pre-set responses or actions you can manually implement during ticket creation or update. Apply them with a single click to perform complex steps efficiently and reduce repetitive tasks.",
    },
    headerText:
      "Automate case resolution by applying macros. Click one at a time and update a ticket with a pre-set series of actions.",
    helpCenterLink: HelpCenter.CREATE_AND_APPLY_MACROS,
    sectionName: "Macros",
    eventPlace: EventPlace.Automations,
    featureFlagName: newFeaturesCampaigns.hasClosedMacrosBanner.flag,
    campaignStartDate: newFeaturesCampaigns.hasClosedMacrosBanner.campaignStartDate,
    image: macrosImage,
  },
  workflows: {
    bannerText: {
      header: "What are rules?",
      content:
        "Rules automate your tasks, triggering actions when specific conditions are met. They cut down on ticket management time, help you respond quicker and more accurately to customer requests, minimize errors, and free you up to tackle complex issues.",
    },
    headerText:
      "Create rules using available conditions and actions to auto-solve cases. Make all your operations smarter, well-timed, and human-free.",
    helpCenterLink: HelpCenter.AUTOMATED_WORKFLOWS,
    sectionName: "Rules",
    eventPlace: EventPlace.Automations,
    featureFlagName: newFeaturesCampaigns.hasClosedWorkflowsBanner.flag,
    campaignStartDate: newFeaturesCampaigns.hasClosedWorkflowsBanner.campaignStartDate,
    image: workflowsImage,
  },
  "contact forms": {
    bannerText: {
      header: "What are contact forms?",
      content:
        "Easily customize and embed contact forms on your website so that visitors can message you directly. Every submission becomes a ticket automatically, making customer service a breeze.",
    },
    headerText: "Add a contact form to your website so your visitors can send messages straight to your HelpDesk.",
    helpCenterLink: HelpCenter.CONTACT_FORM_FOR_YOUR_WEBSITE,
    sectionName: "Contact forms",
    eventPlace: EventPlace.Settings,
    featureFlagName: newFeaturesCampaigns.hasClosedContactFormsBanner.flag,
    campaignStartDate: newFeaturesCampaigns.hasClosedContactFormsBanner.campaignStartDate,
  },
};

interface IProps {
  section: ISection;
  autoDisplayForAllUsers?: boolean;
}

export const SectionOnboardingBanner: FC<IProps> = ({ section, autoDisplayForAllUsers = false }) => {
  const userRole = useSelector(selectLoggedInAgentPermission);

  const { image, bannerText, headerText, helpCenterLink, sectionName, eventPlace, featureFlagName, campaignStartDate } =
    placementProperties[section];

  const {
    featureFlagValue: hasClosedBanner,
    setAgentFlagCallback: markBannerAsClosed,
    isNewForAgent,
  } = useNewFeatureForAgents(featureFlagName, campaignStartDate);

  const [isBannerOpened, setBannerOpened] = useState(!hasClosedBanner && (autoDisplayForAllUsers || !isNewForAgent));

  const hasImage = Boolean(image);
  const sectionNameTestIdPrefix = sectionName.toLocaleLowerCase().replace(/ /g, "-");

  const trackBannerToggle = (isOpen: boolean): void => {
    trackEngagement("Section onboarding banner", eventPlace, {
      role: userRole,
      place: sectionName.toLocaleLowerCase(),
      status: isOpen ? "show clicked" : "hide clicked",
    });
  };

  const handleBannerCloseClick = (): void => {
    markBannerAsClosed();
    setBannerOpened(false);
    trackBannerToggle(false);
  };

  const handleLearnMoreClick = (): void => {
    trackEngagement(`${sectionName}: Learn more clicked`, eventPlace, {
      place: sectionName.toLocaleLowerCase(),
      role: userRole,
      bannerOpened: isBannerOpened,
    });
  };

  const handleInfoButtonClick = (): void => {
    setBannerOpened(true);
    trackBannerToggle(true);
  };

  return isBannerOpened ? (
    <StyledPromoBanner
      onClose={handleBannerCloseClick}
      additionalContent={hasImage ? <img src={image} alt="" /> : undefined}
      css={{
        background: withVar(DesignToken.SurfaceGradient07),
      }}
      hasImage={hasImage}
    >
      <Heading css={{ paddingBottom: withVar(SpacingToken.Spacing1) }}>{bannerText.header}</Heading>
      <Text>
        {bannerText.content}{" "}
        <Link
          bold={false}
          href={helpCenterLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${sectionNameTestIdPrefix}-onboarding-banner-learn-more-link`}
          onClick={handleLearnMoreClick}
        >
          Learn more
        </Link>
      </Text>
    </StyledPromoBanner>
  ) : (
    <Flex justifyContent="space-between" gap={withVar(SpacingToken.Spacing3)}>
      <Text>
        {headerText}{" "}
        <Link
          bold={false}
          href={helpCenterLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${sectionNameTestIdPrefix}-learn-more-link`}
          onClick={handleLearnMoreClick}
        >
          Learn more
        </Link>
      </Text>
      <Button
        kind="plain"
        size="xcompact"
        icon={<Icon source={Info} />}
        onClick={handleInfoButtonClick}
        data-testid={`${sectionNameTestIdPrefix}-show-onboarding-banner-button`}
      />
    </Flex>
  );
};

const StyledPromoBanner = styled(PromoBannerV2)<{ hasImage: boolean }>`
  ${(p): SerializedStyles =>
    p.hasImage
      ? css``
      : css`
          max-width: 940px;
        `};
`;
